import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExperienceTimeline = ({ profile = {} }) => {
  // Extract experiences from profile data
  const experiences = profile?.work_experience && Array.isArray(profile.work_experience) ? profile.work_experience : [];
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpanded = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatDuration = (startDate, endDate) => {
    if (!startDate) return 'Duration not specified';
    
    try {
      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : new Date();
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      
      if (years === 0) {
        return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
      } else if (remainingMonths === 0) {
        return `${years} year${years !== 1 ? 's' : ''}`;
      } else {
        return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
      }
    } catch (error) {
      console.error('Error formatting duration:', error);
      return 'Duration not specified';
    }
  };

  return (
    <section className="content-container py-12" id="experience">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">Work Experience</h2>
      </div>
      
      {experiences.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <Icon name="Briefcase" size={40} className="text-secondary-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium mb-2">No work experience added yet</h3>
          <p className="text-text-secondary mb-4">Work history will appear here once added in profile settings.</p>
        </div>
      ) : (

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-border"></div>

        <div className="space-y-8">
          {experiences.map((experience, index) => (
                <div key={index} className="relative flex gap-6">
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center border-4 border-surface">
                      <Icon name="Briefcase" size={16} color="white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-8">
                    <div 
                      className="card p-6 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => toggleExpanded(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && toggleExpanded(index)}
                    >
                      {/* Header */}
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                        <div className="flex items-start gap-4 flex-1">
                          {experience?.company_logo && (
                            <div className="w-12 h-12 rounded-lg overflow-hidden border border-border flex-shrink-0 bg-secondary-100 flex items-center justify-center">
                              <Icon name="Building" size={24} className="text-secondary-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-text-primary">
                              {experience?.position || experience?.title || 'Virtual Assistant'}
                            </h3>
                            <p className="text-accent font-medium">{experience?.company || experience?.organization || 'Independent'}</p>
                            <p className="text-sm text-text-secondary">
                              {experience?.location || 'Remote'} • {experience?.type || experience?.employment_type || 'Freelance'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm font-medium text-text-primary">
                            {experience.start_date || experience.startDate} - {experience.end_date || experience.endDate || 'Present'}
                          </p>
                          <p className="text-sm text-text-muted">
                            {formatDuration(experience?.start_date, experience?.end_date)}
                          </p>
                          <div className="mt-2 flex justify-end">
                            <Icon 
                              name={expandedItems.has(index) ? 'ChevronUp' : 'ChevronDown'} 
                              size={20}
                              className="text-primary"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Collapsed View - Short Description */}
                      {!expandedItems.has(index) && (
                        <div className="mt-2">
                          <p className="text-text-secondary text-sm line-clamp-2">
                            {experience?.description ? experience.description : 'No description provided'}
                          </p>
                          <p className="text-xs text-primary mt-2 font-medium">Click to expand details</p>
                        </div>
                      )}

                      {/* Expanded View */}
                      {expandedItems.has(index) && (
                        <>
                          {/* Full Description */}
                          <div className="mt-4">
                            <p className="text-text-secondary">
                              {experience?.description ? experience.description : 'No description provided'}
                            </p>
                          </div>

                          {/* Key Achievements */}
                          {experience?.achievements && Array.isArray(experience.achievements) && experience.achievements.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium text-text-primary mb-2">Key Achievements:</h4>
                              <ul className="space-y-1">
                                {experience.achievements.map((achievement, achIndex) => (
                                  <li key={achIndex} className="flex items-start gap-2">
                                    <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                                    <span className="text-sm text-text-secondary">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Responsibilities */}
                          {experience?.responsibilities && Array.isArray(experience.responsibilities) && experience.responsibilities.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium text-text-primary mb-2">Key Responsibilities:</h4>
                              <ul className="list-disc list-inside space-y-1 text-text-secondary">
                                {experience.responsibilities.map((responsibility, respIndex) => (
                                  <li key={respIndex} className="text-sm">
                                    {typeof responsibility === 'object' ? responsibility.description || responsibility.text : responsibility}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Skills Used */}
                          {experience?.skills && Array.isArray(experience.skills) && experience.skills.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium text-text-primary mb-2">Skills Used:</h4>
                              <div className="flex flex-wrap gap-2">
                                {experience.skills.map((skill, skillIndex) => (
                                  <span
                                    key={skillIndex}
                                    className="px-3 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full"
                                  >
                                    {typeof skill === 'object' ? skill.name : skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Client Testimonial */}
                          {experience?.testimonial && (
                            <div className="bg-secondary-50 rounded-lg p-4 border-l-4 border-primary mt-4">
                              <div className="flex items-start gap-3">
                                <Icon name="Quote" size={20} className="text-primary mt-1 flex-shrink-0" />
                                <div>
                                  <p className="text-text-secondary italic mb-2">
                                    "{experience.testimonial?.content || 'Great work!'}"
                                  </p>
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1">
                                      {[...Array(5)].map((_, starIndex) => (
                                        <Icon
                                          key={starIndex}
                                          name="Star"
                                          size={14}
                                          className={`${
                                            starIndex < (experience.testimonial?.rating || 5)
                                              ? 'text-warning fill-current' :'text-secondary-300'
                                          }`}
                                        />
                                      ))}
                                    </div>
                                    <span className="text-sm font-medium text-text-primary">
                                      {experience.testimonial?.clientName || 'Client'}
                                    </span>
                                    <span className="text-xs text-text-muted">
                                      {experience.testimonial?.clientTitle || ''}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Project Outcomes */}
                          {experience?.outcomes && Array.isArray(experience.outcomes) && experience.outcomes.length > 0 && (
                            <div className="mt-4 grid sm:grid-cols-2 gap-4">
                              {experience.outcomes.map((outcome, outcomeIndex) => (
                                <div key={outcomeIndex} className="flex items-center gap-3 p-3 bg-success-50 rounded-lg">
                                  <Icon name="TrendingUp" size={20} className="text-success" />
                                  <div>
                                    <p className="font-medium text-text-primary">{outcome.metric}</p>
                                    <p className="text-sm text-text-secondary">{outcome.description}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
      )}
    </section>
  );
};

export default ExperienceTimeline;
